/**
 * ProgressTracker Component
 *
 * Shows participants where they are in the experiment flow.
 */
import React from "react";

/**
 * Simple progress tracker with numbered steps
 */
export function ProgressTracker({ stages, currentIndex }) {
  return (
    <div className="flex items-center justify-center py-2">
      {stages.map((stage, i) => (
        <div key={stage.id} className="flex items-center">
          {/* Step circle */}
          <div
            className={`
              flex items-center justify-center w-7 h-7 rounded-full
              text-xs font-medium transition-all duration-300
              ${
                i <= currentIndex
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-400"
              }
              ${i === currentIndex ? "ring-2 ring-blue-100" : ""}
            `}
          >
            {i < currentIndex ? (
              <CheckIcon />
            ) : (
              i + 1
            )}
          </div>

          {/* Connector line */}
          {i < stages.length - 1 && (
            <div
              className={`w-8 h-0.5 mx-1 rounded-full transition-colors duration-300 ${
                i < currentIndex ? "bg-blue-500" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Progress tracker with labels below each step
 */
export function ProgressTrackerWithLabels({ stages, currentIndex }) {
  return (
    <div className="w-full max-w-2xl mx-auto py-4">
      <div className="flex items-center justify-between">
        {stages.map((stage, i) => (
          <div key={stage.id} className="flex flex-col items-center flex-1">
            <div
              className={`
                flex items-center justify-center w-8 h-8 rounded-full
                text-xs font-semibold mb-1 transition-all duration-300
                ${
                  i <= currentIndex
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-400"
                }
              `}
            >
              {i < currentIndex ? <CheckIcon /> : i + 1}
            </div>
            <span
              className={`text-xs text-center transition-colors ${
                i === currentIndex
                  ? "text-blue-600 font-medium"
                  : "text-gray-400"
              }`}
            >
              {stage.label}
            </span>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="relative mt-3">
        <div className="h-1 bg-gray-200 rounded-full" />
        <div
          className="absolute top-0 left-0 h-1 bg-blue-500 rounded-full transition-all duration-500"
          style={{
            width: `${(currentIndex / (stages.length - 1)) * 100}%`,
          }}
        />
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
    </svg>
  );
}
